import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Shield, Award, BookOpen, ClipboardCheck, ArrowRight, CheckCircle,
  Scale, Globe, FileText, Users,
  Newspaper, ExternalLink, Calendar, AlertTriangle
} from 'lucide-react';

const featuredExams = [
  {
    code: 'CBEAM',
    title: 'Competency-Based Examination in Aesthetic Medicine',
    target: 'Doctors with Registered Medical License',
    to: '/faculty/aesthetic-medicine'
  },
  {
    code: 'CBELM',
    title: 'Competency-Based Examination in Longevity Medicine',
    target: 'Doctors with Registered Medical License',
    to: '/faculty/longevity-medicine'
  },
  {
    code: 'CBERM',
    title: 'Competency-Based Examination in Regenerative Medicine',
    target: 'Doctors with Registered Medical License',
    to: '/faculty/regenerative-medicine'
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#1B2A4A] imcac-texture" data-testid="hero-section">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1724181100610-c2d7926b1b34?auto=format&fit=crop&w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative imcac-container py-20 md:py-32">
          <div className="max-w-3xl">
            <p className="imcac-overline mb-4 animate-fade-in-up">Global Competency Examination & Certification Board</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight font-heading animate-fade-in-up animate-delay-100">
              Competency-Based Certification for Healthcare Professionals
            </h1>
            <div className="h-1 w-24 bg-[#C5A572] mt-6 mb-6 animate-fade-in-up animate-delay-200" />
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl animate-fade-in-up animate-delay-200">
              Independent assessment and certification of professional competency through standardized examinations, practical assessments, and evidence-based evaluation frameworks.
            </p>
            <div className="flex flex-wrap gap-4 mt-8 animate-fade-in-up animate-delay-300">
              <Link to="/ipcf" data-testid="hero-certifications-btn">
                <Button className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm px-8 py-3 text-base font-medium shadow-md">
                  View Certifications <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/examination-pathways" data-testid="hero-pathways-btn">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm px-8 py-3 text-base">
                  Examination Pathways
                </Button>
              </Link>
              <Link to="/verification" data-testid="hero-verify-btn">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm px-8 py-3 text-base">
                  <Shield className="mr-2 h-4 w-4" /> Verify Certificate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights Stats */}
      <section className="bg-white border-b border-slate-100">
        <div className="imcac-container py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center p-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1B2A4A]/5 rounded-sm mb-3">
                <Globe className="h-6 w-6 text-[#C5A572]" />
              </div>
              <p className="text-sm font-semibold text-[#1B2A4A]">Independent</p>
              <p className="text-xs text-slate-500">Examination & Certification Board</p>
            </div>
            <div className="text-center p-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1B2A4A]/5 rounded-sm mb-3">
                <Users className="h-6 w-6 text-[#C5A572]" />
              </div>
              <p className="text-sm font-semibold text-[#1B2A4A]">International</p>
              <p className="text-xs text-slate-500">Examiners & Assessors</p>
            </div>
            <div className="text-center p-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1B2A4A]/5 rounded-sm mb-3">
                <Shield className="h-6 w-6 text-[#C5A572]" />
              </div>
              <p className="text-sm font-semibold text-[#1B2A4A]">Competency-Based</p>
              <p className="text-xs text-slate-500">Standardized Assessments</p>
            </div>
            <div className="text-center p-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1B2A4A]/5 rounded-sm mb-3">
                <Award className="h-6 w-6 text-[#C5A572]" />
              </div>
              <p className="text-sm font-semibold text-[#1B2A4A]">Post-Licensure</p>
              <p className="text-xs text-slate-500">Professional Certifications</p>
            </div>
          </div>
        </div>
      </section>

      {/* Prominent Disclaimer Banner */}
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-6" data-testid="independence-disclaimer">
        <div className="imcac-container">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-[#1B2A4A] mb-1">Important Notice</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                IMCAC is an independent competency assessment and certification body. IMCAC does not provide training, workshops, academic programs, or medical licenses. Certification does not replace professional registration requirements imposed by national regulatory authorities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Summary */}
      <section className="imcac-section bg-white" data-testid="about-section">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="imcac-overline mb-3">About IMCAC</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1B2A4A] tracking-tight font-heading mb-6">
                Global Competency Examination & Certification Board
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                The International Medical Competency Accreditation Council (IMCAC) is an independent competency assessment and certification body for post-licensure healthcare professionals.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                IMCAC assesses knowledge, clinical reasoning, practical competence, professional judgment, and ethical practice through structured competency-based examinations.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                IMCAC does not provide medical training, workshops, degree programs, academic qualifications, or medical licenses. Our role is to independently assess and certify professional competency through rigorous, standardised evaluation frameworks.
              </p>
              <Link to="/about" data-testid="learn-more-btn">
                <Button variant="outline" className="rounded-sm border-[#1B2A4A] text-[#1B2A4A]">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-5">
              <img
                src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/owme7yia_DSC_1416.JPG"
                alt="IMCAC Competency Assessment"
                className="w-full h-80 object-cover rounded-sm shadow-md"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Certification Verification CTA */}
      <section className="bg-[#1B2A4A] py-12" data-testid="verify-cta-section">
        <div className="imcac-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-sm flex items-center justify-center">
                <Shield className="h-7 w-7 text-[#C5A572]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white font-heading">Verify Certification</h3>
                <p className="text-sm text-slate-300">Confirm the validity of any IMCAC-issued certification instantly</p>
              </div>
            </div>
            <Link to="/verification" data-testid="verify-cta-btn">
              <Button className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm px-8 py-3 text-base font-medium">
                Verify Now <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Board Certifications - Examinations */}
      <section className="imcac-section bg-white" data-testid="cbeam-section">
        <div className="imcac-container">
          <div className="text-center mb-12">
            <p className="imcac-overline mb-3">Board Certification</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1B2A4A] tracking-tight font-heading mb-4">
              Competency-Based Examinations
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Successful candidates are awarded the designation of Board Certified in their respective specialty area by IMCAC.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredExams.map((exam, i) => (
              <Card key={i} className="imcac-card border-t-2 border-t-[#C5A572] animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <CardContent className="p-6">
                  <Badge className="bg-[#1B2A4A] text-white text-xs mb-3">{exam.code}</Badge>
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-2 leading-tight">{exam.title}</h3>
                  <p className="text-sm text-slate-600 mb-4">{exam.target}</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {['Written Exam', 'OSCE', 'Case Log', 'Viva'].map((item, j) => (
                      <div key={j} className="flex items-center gap-1 text-xs text-slate-600">
                        <CheckCircle className="h-3 w-3 text-[#C5A572] flex-shrink-0" /> {item}
                      </div>
                    ))}
                  </div>
                  <Link to={exam.to} data-testid={`exam-${exam.code.toLowerCase()}-link`}>
                    <Button className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm text-sm">
                      View Details <ArrowRight className="ml-2 h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/examination-pathways" data-testid="view-all-exams">
              <Button variant="outline" className="rounded-sm border-[#1B2A4A] text-[#1B2A4A]">
                View All Examinations <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Assessment Framework */}
      <section className="imcac-section bg-[#F8FAFC]" data-testid="assessment-section">
        <div className="imcac-container">
          <div className="text-center mb-12">
            <p className="imcac-overline mb-3">Assessment Methodology</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1B2A4A] tracking-tight font-heading mb-4">
              Rigorous Competency Assessment
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              IMCAC examinations assess candidates through multiple evidence-based assessment components designed to evaluate real-world clinical competency.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: FileText, label: 'Written Examination', desc: 'Knowledge & clinical reasoning' },
              { icon: ClipboardCheck, label: 'Clinical Scenarios', desc: 'Case analysis & decision-making' },
              { icon: Users, label: 'Viva Voce', desc: 'Professional judgment assessment' },
              { icon: Scale, label: 'OSCE', desc: 'Practical competence evaluation' },
              { icon: BookOpen, label: 'Portfolio Review', desc: 'Evidence of clinical practice' },
              { icon: Shield, label: 'Professional Conduct', desc: 'Ethical practice assessment' },
            ].map((item, i) => (
              <Card key={i} className="imcac-card text-center animate-fade-in-up" style={{ animationDelay: `${i * 80}ms` }}>
                <CardContent className="p-4">
                  <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center mx-auto mb-3">
                    <item.icon className="h-5 w-5 text-[#C5A572]" />
                  </div>
                  <p className="text-sm font-semibold text-[#1B2A4A] font-heading mb-1">{item.label}</p>
                  <p className="text-xs text-slate-500">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CPD Recognition Section */}
      <section className="imcac-section bg-white" data-testid="cpd-recognition-section">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="imcac-overline mb-3">CPD Recognition Service</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1B2A4A] tracking-tight font-heading mb-6">
                CPD Recognition for Educational Activities
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                IMCAC evaluates educational activities delivered by independent organizations and may assign CPD credit values based on educational quality, learning outcomes, and participant engagement.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                IMCAC does not provide training or educational programs directly. CPD Recognition is an independent evaluation service.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/cpd-recognition/apply" data-testid="cpd-apply-btn">
                  <Button className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm px-6">
                    Apply for CPD Recognition <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/cpd-recognition/recognized-activities" data-testid="cpd-directory-btn">
                  <Button variant="outline" className="rounded-sm border-[#1B2A4A] text-[#1B2A4A]">
                    View Recognized Activities
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <Card className="imcac-card border-l-4 border-l-[#C5A572]">
                <CardContent className="p-6 space-y-4">
                  {[
                    { level: 'Level 1', title: 'IMCAC CPD Recognized', badge: 'bg-blue-100 text-blue-800' },
                    { level: 'Level 2', title: 'IMCAC CPD Quality Assured', badge: 'bg-[#C5A572]/20 text-[#9A7D4E]' },
                    { level: 'Level 3', title: 'IMCAC Advanced CPD Recognized', badge: 'bg-[#1B2A4A] text-white' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm">
                      <Badge className={`${item.badge} text-xs`}>{item.level}</Badge>
                      <span className="text-sm font-medium text-[#1B2A4A]">{item.title}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* News & Media Section */}
      <section className="bg-[#F8FAFC] py-16" data-testid="news-section">
        <div className="imcac-container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#1B2A4A]/5 rounded-sm mb-3">
                <Newspaper className="h-3 w-3 text-[#C5A572]" />
                <span className="text-xs font-semibold tracking-wider text-[#1B2A4A] uppercase">Press Coverage</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1B2A4A] font-heading">News & Media</h2>
            </div>
            <Link to="/news-media">
              <Button variant="outline" className="rounded-sm border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white hidden md:flex">
                View All <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="overflow-hidden hover:shadow-lg transition-shadow group">
              <CardContent className="p-0">
                <div className="h-48 overflow-hidden">
                  <img 
                    src="https://feeds.abplive.com/onecms/images/uploaded-images/2026/03/03/2e86a6b490fcf1375846e41c095e767a1772525719630401_original.jpg?impolicy=abp_cdn&imwidth=1200&height=675" 
                    alt="ABP Live Article"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className="bg-[#1B2A4A] text-white text-xs rounded-sm">ABP Live</Badge>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Mar 3, 2026
                    </span>
                  </div>
                  <h3 className="font-bold text-[#1B2A4A] font-heading mb-2 line-clamp-2">
                    Global Experts Call For Higher Safety Standards In Cosmetic Practice
                  </h3>
                  <a 
                    href="https://news.abplive.com/infotainment/global-experts-call-for-higher-safety-standards-in-cosmetic-practice-1829607"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#C5A572] font-medium flex items-center gap-1 hover:underline"
                  >
                    Read Article <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-lg transition-shadow group">
              <CardContent className="p-0">
                <div className="h-48 overflow-hidden">
                  <img 
                    src="https://news24online.com/wp-content/uploads/2026/02/news-24-2-11.png" 
                    alt="News24 Article"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className="bg-[#1B2A4A] text-white text-xs rounded-sm">News24</Badge>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Feb 18, 2026
                    </span>
                  </div>
                  <h3 className="font-bold text-[#1B2A4A] font-heading mb-2 line-clamp-2">
                    The Global Shift from Attendance-Based to Competency-Based Certification in Aesthetic Medicine
                  </h3>
                  <a 
                    href="https://news24online.com/information/the-global-shift-from-attendance-based-to-competency-based-certification-in-aesthetic-medicine/751693/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#C5A572] font-medium flex items-center gap-1 hover:underline"
                  >
                    Read Article <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 text-center md:hidden">
            <Link to="/news-media">
              <Button variant="outline" className="rounded-sm border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white">
                View All News <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="bg-[#F8FAFC] py-10" data-testid="disclaimer-section">
        <div className="imcac-container">
          <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8">
            <div className="flex items-start gap-4">
              <Shield className="h-6 w-6 text-[#C5A572] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-[#1B2A4A] font-heading mb-2">Legal Disclaimer</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  IMCAC is an independent competency assessment and certification body. IMCAC does not provide training, workshops, academic programs, or medical licenses. Certification does not replace professional registration requirements imposed by national regulatory authorities. Certifications are awarded based on successful completion of competency-based assessments and do not constitute a license to practice. IMCAC is a registered Non-Profit Organisation under Section 8 of the Companies Act, India.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
